import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChildren,
} from '@angular/core';
import { RRIBaseModel } from '../../../common/interfaces/base-model.interface';
import { RRTableAction } from '../../../common/types/table-action.type';
import { RRITableFieldSettings } from '../../../common/interfaces/table/table-field-settings.interface';
import { Router } from '@angular/router';
import { RRSpinnerModel } from '../../../spinner/models/spinner.model';
import { RR_TABLE_FIELD_TYPE } from '../../../common/enum/table/table-field-type.enum';
import { RR_TABLE_ACTION_TYPE } from '../../../common/enum/table/table-action-type.enum';
import { RR_THUMBNAIL } from '../../../common/constants/rr_thumbnail';
import { RRTableModel } from '../../../common/models/table/table.model';
import { RRITableActionOutput } from '../../../common/interfaces/table/table-action-output.interface';
import { RRISort } from '../../../common/interfaces/base-sort.interface';
import { RRITableRowClick } from '../../../common/interfaces/table/table-row-click.interface';
import { RR_CLICK_TYPE } from '../../../common/enum/click-type.enum';
import { RR_TABLE_ACTION_BEHAVIOUR_TYPE } from '../../../common/enum/table/table-action-behaviour-type.enum';
import { RRITableEditActionSettings } from '../../../common/interfaces/table/table-edit-action-settings.interface';
import { RRITag } from '../../../tags/public-api';

@Component({
  selector: 'rr-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent<T extends RRIBaseModel>
  implements OnInit, OnChanges, AfterContentInit
{
  public readonly TABLE_FIELD_TYPE = RR_TABLE_FIELD_TYPE;
  public readonly TABLE_ACTION_TYPE = RR_TABLE_ACTION_TYPE;
  public readonly RR_THUMBNAIL = RR_THUMBNAIL;
  public editColumClass: string;
  public selectAll: boolean;
  public tooltipEnabled: boolean;
  public showActionList: boolean;
  public disableHoverAction: boolean = true;

  private _contentHasLoaded: boolean;
  @Input() spinnerStatus: RRSpinnerModel;
  @Input() selectedRadioEntity: T;
  @Input() selectedEntities: T[];
  @Input() model: RRTableModel<T>;
  @Input() clearRadioSelection: boolean;
  @Output() sortBy: EventEmitter<RRISort<T>> = new EventEmitter();
  @Output() actionClicked: EventEmitter<RRITableActionOutput<T>> =
    new EventEmitter();
  @Output() rowsSelected: EventEmitter<T[]> = new EventEmitter();
  @Output() rowClicked: EventEmitter<RRITableRowClick<T>> = new EventEmitter();
  @Output() radioSelected: EventEmitter<T> = new EventEmitter();
  @Output() radioSelectionCleared: EventEmitter<void> = new EventEmitter();

  @ViewChildren('actionButton') actionButton: QueryList<ElementRef>;
  @ViewChildren('tableRowElement') tableRowElements: QueryList<ElementRef>;

  constructor(
    private _rendered: Renderer2,
    private _router: Router,
    private _cdRef: ChangeDetectorRef,
  ) {
    this.selectAll = false;
    this._contentHasLoaded = false;
  }

  ngOnInit(): void {
    if (this.model && this.model.tableActions) {
      this.model.tableActions.sort(
        (a, b) => a.settings.order - b.settings.order,
      );
    }

    if (!this.selectedEntities) {
      this.selectedEntities = [];
    }
  }

  ngOnChanges(): void {
    let tableActionsCounter =
      this.model && this.model.tableActions
        ? this.model.tableActions.length
        : 0;
    if (this.clearRadioSelection === true) {
      this.selectedRadioEntity = undefined;
      this.radioSelectionCleared.emit();
    }
    const actionsNotInColumn = this.model.tableActions.filter(
      (action: RRTableAction<T>) => !action.settings.showInColumn,
    );

    if (tableActionsCounter) {
      tableActionsCounter = tableActionsCounter - actionsNotInColumn.length;

      this.editColumClass = 'edit-button';
    }
  }

  ngAfterContentInit(): void {
    this._contentHasLoaded = true;
  }

  public getTags(field: RRITableFieldSettings<T>, row: T): RRITag[] {
    function instanceOfRRITag(object: any): object is RRITag[] {
      if (Array.isArray(object)) {
        return object.some((t) => 'name' in t);
      } else {
        return 'name' in object;
      }
    }
    if (instanceOfRRITag(row[field.fieldName])) {
      return row[field.fieldName] as any;
    }
  }
  public trackByType(index: number, action: RRTableAction<T>) {
    return action.type;
  }

  public onSortBy(fieldName: keyof T, desc: boolean): void {
    this.sortBy.emit({ fieldName, desc });
  }

  public onActionClicked(action: RRTableAction<T>, tableRow: T): void {
    this.actionClicked.emit({
      action: action,
      row: tableRow,
    });
  }

  public onActionMiddleClicked(action: RRTableAction<T>, tableRow: T): void {
    this.manageEditAction(action.settings, tableRow);
  }

  public onRefreshClicked(): void {
    this.actionClicked.emit({
      action: this.getTableRefreshAction(),
      row: null,
    });
  }

  public checkSort(fieldName: keyof T, desc: boolean): boolean {
    return (
      this.model.sortBy &&
      this.model.sortBy.fieldName === fieldName &&
      this.model.sortBy.desc === desc
    );
  }

  public showColumn(field: RRITableFieldSettings<T>): boolean {
    return (
      !field.hide &&
      (!this.model.visibleColumns ||
        (this.model.visibleColumns &&
          this.model.visibleColumns.includes(field.fieldName)))
    );
  }

  public hideRow(index: number): boolean {
    return this.model.invisibleRows && this.model.invisibleRows.includes(index);
  }

  public outputTableValue(field: RRITableFieldSettings<T>, row: T): string {
    if (field && field.format && row) {
      return field.format(field, row);
    }

    if (row && field && field.fieldName && row[field.fieldName]) {
      return `${row[field.fieldName]}`;
    }

    return '';
  }

  public outputTableTooltip(field: RRITableFieldSettings<T>, row: T): string {
    if (field && field.tooltip && typeof field.tooltip === 'string') {
      return field.tooltip;
    } else if (
      field &&
      field.tooltip &&
      typeof field.tooltip !== 'string' &&
      row
    ) {
      return field.tooltip(field, row);
    }
    if (field && field.format && row) {
      return field.format(field, row);
    }

    if (row && field && field.fieldName && row[field.fieldName]) {
      return `${row[field.fieldName]}`;
    }

    return '';
  }

  public onRowClick(row: T): void {
    this.rowClicked.emit({ row, clickType: RR_CLICK_TYPE.LEFT });
  }

  public onRowCtrlClick(row: T): void {
    this.rowClicked.emit({ row, clickType: RR_CLICK_TYPE.CTRL });
  }

  public onRowMiddleClick(row: T): void {
    this.rowClicked.emit({ row, clickType: RR_CLICK_TYPE.MIDDLE });
  }

  public onHover(event: any, field: RRITableFieldSettings<T>) {
    if (
      event?.nativeElement?.offsetWidth < event?.nativeElement?.scrollWidth ||
      field?.tooltip
    ) {
      this.tooltipEnabled = true;
    } else {
      this.tooltipEnabled = false;
    }
  }

  public outputTableClass(field: RRITableFieldSettings<T>, row: T): string {
    if (!field && !field.fieldClass && !row) {
      return '';
    }
    if (typeof field.fieldClass === 'function') {
      return field.fieldClass(field, row);
    }
    return typeof field.fieldClass === 'string' ? field.fieldClass : '';
  }

  public isBatchSelectAllChecked(): string {
    let checkAllTableColumnsSelected = false;

    if (this.selectedEntities.length) {
      checkAllTableColumnsSelected = this.model.results.every((tableItem) => {
        return this.selectedEntities.find(
          (x) => x && tableItem && x.id == tableItem.id,
        );
      });
    }

    this.selectAll = checkAllTableColumnsSelected;

    return checkAllTableColumnsSelected ? 'checked' : 'no-check';
  }

  public batchSelectAll(): void {
    this.selectAll = !this.selectAll;

    if (this.selectAll) {
      for (let i = 0; i < this.model.results.length; i++) {
        this.batchSelectChange(this.model.results[i], true);
      }
    } else {
      for (let i = 0; i < this.model.results.length; i++) {
        this.batchSelectChange(this.model.results[i], false);
      }
    }
  }

  public batchSelectChange(tableItem: T, select: boolean = false): void {
    const selectedEntity = this.selectedEntities.find(
      (x) => x && tableItem && x.id === tableItem.id,
    );

    if (selectedEntity) {
      if (!select) {
        this.selectedEntities = this.selectedEntities.filter(
          (e) => e && e.id !== selectedEntity.id,
        );
      }

      if (!this.selectedEntities.length) {
        this.selectedEntities = [];
      }
    } else {
      this.selectedEntities.push(tableItem);
    }

    this.rowsSelected.emit(this.selectedEntities);
  }
  public batchRadioChange(tableItem: T, _select: boolean = false): void {
    this.selectedRadioEntity = tableItem;
    this.radioSelected.emit(tableItem);
  }

  public checkBatchItemChecked(tableRow: T): boolean {
    return this.selectedEntities.some(
      (x: T) => x && tableRow && x.id === tableRow.id,
    );
  }
  public checkBatchItemRadioChecked(tableRow: T): boolean {
    if (!this.selectedRadioEntity) {
      return false;
    }
    return this.selectedRadioEntity.id === tableRow.id;
  }

  public getActionsSectionHeight(index: number): {} {
    let element = null;
    if (this.tableRowElements && this.tableRowElements.length !== 0) {
      if (this.tableRowElements.get) {
        element = this.tableRowElements.get(index);
      }
    }
    const height = element ? element.nativeElement.offsetHeight : null;
    return height ? { height: `${height}px` } : {};
  }

  public getActionIconClass(action: RRTableAction<T>): string {
    if (action.settings && action.settings.icon) {
      return action.settings.icon;
    }

    switch (action.type) {
      case RR_TABLE_ACTION_TYPE.EDIT:
        return 'oi oi-pencil';
      case RR_TABLE_ACTION_TYPE.DELETE:
        return 'oi oi-trash';
      case RR_TABLE_ACTION_TYPE.GRAPH:
        return 'oi oi-fork';
      case RR_TABLE_ACTION_TYPE.LIGHTBOX:
        return 'oi oi-eye';
      case RR_TABLE_ACTION_TYPE.REVISION:
        return 'oi oi-timer';
      case RR_TABLE_ACTION_TYPE.DOWNLOAD:
        return 'oi oi-data-transfer-download';
      default:
        return '';
    }
  }
  public getActionTooltip(action: RRTableAction<T>): string {
    if (action.settings && action.settings.tooltip) {
      return action.settings.tooltip;
    }

    switch (action.type) {
      case RR_TABLE_ACTION_TYPE.EDIT:
        return 'Edit';
      case RR_TABLE_ACTION_TYPE.DELETE:
        return 'Delete';
      case RR_TABLE_ACTION_TYPE.GRAPH:
        return 'Graph';
      case RR_TABLE_ACTION_TYPE.LIGHTBOX:
        return 'Preview';
      case RR_TABLE_ACTION_TYPE.REVISION:
        return 'Revision';
      case RR_TABLE_ACTION_TYPE.DOWNLOAD:
        return 'Download';
      case RR_TABLE_ACTION_TYPE.CUSTOM:
      default:
        return '';
    }
  }

  public showAction(action: RRTableAction<T>): boolean {
    return (
      action.settings && action.settings.showInColumn && !action.settings.hide
    );
  }
  public getActionCounter(action: RRTableAction<T>[]): number {
    return action.filter((x) => x.settings.showInColumn && !x.settings.hide)
      .length;
  }

  public tableHasRefreshAction(): boolean {
    return this.getTableRefreshAction() !== undefined;
  }

  public showActions() {
    this.showActionList = !this.showActionList;
  }

  public onDropdownClose(index: number) {
    if (!this.disableHoverAction) {
      this.toggleActions(index);
    }
  }

  private toggleActions(index: number) {
    let element: ElementRef;
    if (this.actionButton && !Array.isArray(this.actionButton)) {
      element = this.actionButton.toArray()[index];
    }
    if (element && typeof element.nativeElement !== 'undefined') {
      const tdElement = element.nativeElement.parentElement.parentElement;

      if (element.nativeElement.className !== 'close-icon') {
        element.nativeElement.ariaHidden = true;
        element.nativeElement.className = 'close-icon';
        element.nativeElement.innerHTML = '&times;';
        this._rendered.addClass(tdElement, 'rr-item-open');
        return;
      }
      element.nativeElement.ariaHidden = undefined;
      element.nativeElement.className = 'oi oi-ellipses more-icon';
      element.nativeElement.innerHTML = '';
      this._rendered.removeClass(tdElement, 'rr-item-open');
    }
  }
  private getTableRefreshAction(): RRTableAction<T> {
    return this.model.tableActions.find(
      (action: RRTableAction<T>) =>
        action.type === RR_TABLE_ACTION_TYPE.REFRESH,
    );
  }

  private manageEditAction(
    settings: RRITableEditActionSettings<T>,
    row?: T,
  ): void {
    if (
      settings.behaviourType ===
        RR_TABLE_ACTION_BEHAVIOUR_TYPE.NAVIGATE_TO_URL &&
      settings.formatNavigateToUrl &&
      row &&
      row.id
    ) {
      const url = this._router.serializeUrl(
        this._router.createUrlTree([settings.formatNavigateToUrl(row)]),
      );
      window.open(url, '_blank');
    }
  }
}
